// ...existing imports
import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";

const SSLCertificateCheckerContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline */}
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ fontSize: "1.75rem" }}
        gutterBottom
      >
        Check SSL Certificate Status Instantly{" "}
        <SecurityIcon color="primary" sx={{ verticalAlign: "middle" }} />
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Verify SSL Expiry, Validity, Issuer & More in Real-Time
      </Typography>

      {/* Problem & Solution */}
      <Typography variant="body1" paragraph>
        Expired or misconfigured SSL certificates can lead to website downtime,
        trust issues, and SEO penalties. Manual monitoring is unreliable and
        inefficient.
      </Typography>
      <Typography
        variant="body1"
        paragraph
        sx={{ fontWeight: "bold", color: "primary.main" }}
      >
        Our SSL Certificate Checker instantly scans your domain to provide
        expiry date, issuer details, and certificate validity – so you can
        prevent outages and stay secure.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Use Our SSL Certificate Checker?
      </Typography>
      <ul
        style={{
          textAlign: "left",
          display: "inline-block",
          listStyleType: "none",
          paddingLeft: 0,
        }}
      >
        <li>
          ✅ <strong>Instant Scan:</strong> Get SSL details in seconds
        </li>
        <li>
          🔍 <strong>Detailed Report:</strong> Issuer, valid-from, expiry date,
          and status
        </li>
        <li>
          🚫 <strong>Avoid Downtime:</strong> Know before your certificate
          expires
        </li>
        <li>
          🌐 <strong>Works on Any Domain:</strong> Just enter and check
        </li>
        <li>
          🔒 <strong>100% Secure:</strong> We never store your domain data
        </li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How the SSL Checker Works
      </Typography>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          p: 3,
          borderRadius: 2,
          textAlign: "left",
          display: "inline-block",
        }}
      >
        <ol>
          <li>
            <strong>Enter</strong> the domain (e.g., <code>example.com</code>)
          </li>
          <li>
            <strong>Click</strong> the “Check SSL” button
          </li>
          <li>
            <strong>We Fetch</strong> the SSL certificate from the server
          </li>
          <li>
            <strong>View</strong> the results: issuer, validity, expiration, and more
          </li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Trusted by Agencies, IT Teams & Developers
      </Typography>
      <Box sx={{ fontStyle: "italic", mb: 3 }}>
        <Typography paragraph>
          "We monitor all our client domains with this SSL tool – it’s fast,
          accurate, and reliable."
        </Typography>
        <Typography>- Priya S., Web Security Consultant</Typography>
      </Box>

      {/* FAQ Section */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: "inline-block" }}>
        <Typography variant="body1" fontWeight="bold">
          🔐 What details can I check?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          You can check the domain's SSL issuer, valid-from and expiry date,
          certificate status, and days remaining.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          ⏱️ How fast is the SSL scan?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Most SSL lookups complete in under 3 seconds.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🌍 Can I check domains with subpaths or URLs?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Yes – our system automatically extracts the domain even from full
          URLs.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🆓 Is the SSL Certificate Checker free?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Free users consume 1 credit per SSL check. Paid users get unlimited
          checks at no cost 🎁.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          ❓ How to check an SSL certificate?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Simply enter your website domain into our SSL Certificate Checker and
          click "Check SSL". You'll instantly get the certificate's issuer,
          validity status, expiration date, and more – all in one place.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          📅 How to check expiry date of an SSL certificate?
        </Typography>
        <Typography variant="body2" paragraph>
          Our tool shows the exact expiry date and days remaining before your
          SSL certificate expires. Just enter your domain and you'll get the
          information immediately – no technical knowledge required.
        </Typography>
      </Box>

      {/* CTA */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: "bold" }}>
          Ready to check your website's SSL certificate?
        </Typography>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={scrollToTop}
          sx={{
            fontSize: "1.1rem",
            py: 1.5,
            px: 4,
            fontWeight: "bold",
          }}
        >
          Check SSL Now →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 credit per check for Free users. Unlimited for Paid users — no
          credit needed.
        </Typography>
      </Box>
    </Container>
  );
};

export default SSLCertificateCheckerContent;
